import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button } from 'transfix-ui/components/Button'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { Spinner } from 'transfix-ui/components/Spinner'
import { Box, Typography } from 'transfix-ui/core'
import { useUserDetails } from 'utils/hooks/useUserDetails'
import { capitalize } from 'utils/capitalize'
import { REGION_ABBREVIATIONS } from 'constants/countriesStates'
import { costControlMessage } from '../constants/costControlConfig'
import { RULE_TYPES, ICostControlConfig } from '../types/costControlConfig'
import {
  getCostControlConfigs,
  openAddCostControlConfigModal,
} from '../utils/actions/costControlConfig'
import {
  selectCostControlConfigError,
  selectCostControlConfigFetching,
  selectCostControlConfigs,
} from '../utils/reducers/costControlConfig'
import { AddCostControlConfigModal } from './AddCostControlConfigModal'
import { CostControlConfigsTable } from './CostControlConfigsTable'
import { DeleteCostControlConfigModal } from './DeleteCostControlConfigModal'

interface ISortedConfigs {
  baseline?: ICostControlConfig[]
  northeast?: ICostControlConfig[]
  midwest?: ICostControlConfig[]
  southeast?: ICostControlConfig[]
  other?: ICostControlConfig[]
}

type ConfigCategory = keyof ISortedConfigs

export const CostControlPanel = () => {
  const dispatch = useDispatch()
  const error = useSelector(selectCostControlConfigError)
  const fetching = useSelector(selectCostControlConfigFetching)
  const costControlConfigs = useSelector(selectCostControlConfigs)
  const { userPermissions } = useUserDetails()
  const canWriteCostControlConfigs = userPermissions?.canWriteCostControlConfigs
  const openAddModal = () => {
    dispatch(openAddCostControlConfigModal())
  }

  useEffect(() => {
    dispatch(getCostControlConfigs())
  }, [])

  const sortedConfigs = costControlConfigs.reduce(
    (result: ISortedConfigs, config: ICostControlConfig): ISortedConfigs => {
      if (config.ruleType === RULE_TYPES.baseline) {
        return { ...result, baseline: [...(result.baseline || []), config] }
      }
      if (config.pickupRegion === REGION_ABBREVIATIONS.N) {
        return { ...result, northeast: [...(result.northeast || []), config] }
      }
      if (config.pickupRegion === REGION_ABBREVIATIONS.MW) {
        return { ...result, midwest: [...(result.midwest || []), config] }
      }
      if (config.pickupRegion === REGION_ABBREVIATIONS.SE) {
        return { ...result, southeast: [...(result.southeast || []), config] }
      }
      return { ...result, other: [...(result.other || []), config] }
    },
    {}
  )

  const categories: ConfigCategory[] = ['baseline', 'northeast', 'midwest', 'southeast', 'other']
  const tables = categories
    .filter((category: ConfigCategory) => !!sortedConfigs[category])
    .map((category: ConfigCategory) => (
      <div className='mb3' key={category}>
        <Typography variant='h2' className='mb2 b'>
          {category === 'baseline' ? 'Default' : capitalize(category)}
        </Typography>
        <CostControlConfigsTable
          costControlConfigs={sortedConfigs[category] || []}
          readOnly={!canWriteCostControlConfigs}
        />
      </div>
    ))

  return (
    <Box p={4}>
      <div className='flex justify-between mb4'>
        <div className='flex-grow'>
          <InlineMessage type={INLINE_MESSAGE_VARIANTS.INFO} message={costControlMessage} />
        </div>
        {canWriteCostControlConfigs && (
          <div className='no-shrink ml3 tr'>
            <Button color='primary' onClick={openAddModal} data-test='add-cost-config-button'>
              Add New Rule
            </Button>
          </div>
        )}
      </div>
      {error && <InlineMessage type={INLINE_MESSAGE_VARIANTS.ALERT} message={error} />}
      {fetching && (
        <div className='container flex items-center justify-center'>
          <Spinner position='relative' />
        </div>
      )}
      {!fetching && <div>{tables}</div>}
      <AddCostControlConfigModal />
      <DeleteCostControlConfigModal />
    </Box>
  )
}
